/** Hide risk profiler during onboarding and in profile until re-enabled. */
export const SKIP_RISK_PROFILER_ONBOARDING = false

export function isRiskProfilerOnboardingRequired(): boolean {
  return !SKIP_RISK_PROFILER_ONBOARDING
}

export function splitFullName(fullName: string): { firstName: string; lastName: string } {
  const trimmed = fullName.trim()
  if (!trimmed) return { firstName: '', lastName: '' }

  const spaceIndex = trimmed.indexOf(' ')
  if (spaceIndex === -1) return { firstName: trimmed, lastName: '' }

  return {
    firstName: trimmed.slice(0, spaceIndex),
    lastName: trimmed.slice(spaceIndex + 1).trim(),
  }
}

export function combineFullName(firstName: string, lastName: string): string {
  return `${firstName.trim()} ${lastName.trim()}`.trim()
}

/** Age in full years from date of birth (null if DOB missing or invalid). */
export function calculateAge(dateOfBirth: Date | null): number | null {
  if (!dateOfBirth || Number.isNaN(dateOfBirth.getTime())) return null

  const today = new Date()
  let age = today.getFullYear() - dateOfBirth.getFullYear()
  const monthDiff = today.getMonth() - dateOfBirth.getMonth()
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dateOfBirth.getDate())) {
    age -= 1
  }
  return age >= 0 ? age : null
}

export function parseDateOfBirth(value: unknown): Date | null {
  if (!value) return null
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value
  }
  const str = String(value).trim()
  if (!str) return null
  const isoMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (isoMatch) {
    const parsed = new Date(Number(isoMatch[1]), Number(isoMatch[2]) - 1, Number(isoMatch[3]))
    return Number.isNaN(parsed.getTime()) ? null : parsed
  }
  const parsed = new Date(str)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

/** Display date of birth as DD/MM/YYYY (matches onboarding date picker). */
export function formatDateOfBirthDisplay(dateOfBirth: Date | null): string {
  if (!dateOfBirth || Number.isNaN(dateOfBirth.getTime())) return '—'
  const d = String(dateOfBirth.getDate()).padStart(2, '0')
  const m = String(dateOfBirth.getMonth() + 1).padStart(2, '0')
  const y = dateOfBirth.getFullYear()
  return `${d}/${m}/${y}`
}

/** API date-only string (YYYY-MM-DD) in local calendar. */
export function formatDateOfBirthForApi(dateOfBirth: Date | null): string | undefined {
  if (!dateOfBirth || Number.isNaN(dateOfBirth.getTime())) return undefined
  const y = dateOfBirth.getFullYear()
  const m = String(dateOfBirth.getMonth() + 1).padStart(2, '0')
  const d = String(dateOfBirth.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

export type BasicDetailsFormValues = {
  firstName: string
  lastName: string
  dateOfBirth: Date | null
  countryOfResidence: string
  taxResidency: string
  citizenship: string
  callingPhone: string
  clientCv: string
}

/** Email is read-only account identity, so it lives outside the editable form values. */
export type BasicDetailsProfile = BasicDetailsFormValues & {
  email: string
  basicDetailsCompleted?: boolean
  riskProfileCompleted?: boolean
  riskProfilerRequired?: boolean
}

export function isBasicDetailsComplete(profile: Partial<BasicDetailsProfile>): boolean {
  if (profile.basicDetailsCompleted) return true

  return Boolean(
    profile.firstName?.trim() &&
      profile.lastName?.trim() &&
      profile.dateOfBirth &&
      !Number.isNaN(profile.dateOfBirth.getTime()) &&
      profile.countryOfResidence?.trim() &&
      profile.taxResidency?.trim() &&
      profile.citizenship?.trim() &&
      profile.callingPhone?.trim(),
  )
}

export type RiskProfilerRequiredSource = {
  riskProfilerRequired?: boolean | null
  risk_profiler_required?: boolean | null
  riskProfileCompleted?: boolean | null
  risk_profile_completed?: boolean | null
}

/** Falls back to !risk_profile_completed when the API omits `risk_profiler_required`. */
export function resolveRiskProfilerRequired(data: RiskProfilerRequiredSource = {}): boolean {
  if (!isRiskProfilerOnboardingRequired()) return false
  if (typeof data.riskProfilerRequired === 'boolean') return data.riskProfilerRequired
  if (typeof data.risk_profiler_required === 'boolean') return data.risk_profiler_required
  const completed = data.riskProfileCompleted ?? data.risk_profile_completed
  return completed !== true
}

export function getOnboardingFlagsFromProfileData(data: {
  basicDetailsCompleted?: boolean
  basic_details_completed?: boolean
  riskProfileCompleted?: boolean
  risk_profile_completed?: boolean
  riskProfilerRequired?: boolean
  risk_profiler_required?: boolean
}): {
  basicDetailsCompleted: boolean
  riskProfileCompleted: boolean
  riskProfilerRequired: boolean
} {
  const riskProfileCompleted = Boolean(data.riskProfileCompleted ?? data.risk_profile_completed)
  return {
    basicDetailsCompleted: Boolean(data.basicDetailsCompleted ?? data.basic_details_completed),
    riskProfileCompleted,
    riskProfilerRequired: resolveRiskProfilerRequired(data),
  }
}

/** General profile tab is read-only after onboarding basic details are submitted. */
export function isGeneralProfileLocked(basicDetailsCompleted?: boolean): boolean {
  return Boolean(basicDetailsCompleted)
}

export function mapApiProfileToBasicDetails(
  data: Record<string, unknown>,
  email: string,
): BasicDetailsProfile {
  const callingPhone = String(data.phone ?? data.phoneNumber ?? '')

  const apiFirstName = String(data.first_name ?? data.firstName ?? '')
  const apiLastName = String(data.last_name ?? data.lastName ?? '')
  const fromFullName = splitFullName(String(data.name ?? ''))

  const dateOfBirth = parseDateOfBirth(data.date_of_birth ?? data.dateOfBirth ?? data.dob ?? null)

  return {
    firstName: apiFirstName || fromFullName.firstName,
    lastName: apiLastName || fromFullName.lastName,
    dateOfBirth,
    countryOfResidence: String(data.country ?? data.countryOfResidence ?? ''),
    taxResidency: String(data.tax_residency ?? data.taxResidency ?? ''),
    citizenship: String(data.citizenship ?? ''),
    email: String(data.email ?? email),
    callingPhone,
    clientCv: String(data.occupation ?? data.clientCv ?? data.client_cv ?? ''),
    basicDetailsCompleted: Boolean(data.basic_details_completed ?? data.basicDetailsCompleted),
    riskProfileCompleted: Boolean(data.risk_profile_completed ?? data.riskProfileCompleted),
    riskProfilerRequired: resolveRiskProfilerRequired(data),
  }
}

export type OnboardingPath =
  | '/onboarding/basic-details'
  | '/onboarding/risk-profiler'
  | '/opportunities'

/** Default destination once mandatory onboarding steps are satisfied. */
export const POST_ONBOARDING_PATH = '/opportunities' as const

/** Superuser sessions act on a client's behalf and are exempt from onboarding gates. */
export function isSuperuserSession(adminRole?: string | null): boolean {
  return Boolean(adminRole)
}

/** Confidentiality agreement is only required after the risk profiler has been submitted. */
export function shouldPromptConfidentialityAgreement(
  riskProfileCompleted: boolean,
  acceptedNDA: boolean,
): boolean {
  return Boolean(riskProfileCompleted && !acceptedNDA)
}

export function resolveOnboardingPathFromFlags(
  basicDetailsCompleted: boolean,
  riskProfilerRequired = false,
  isSuperuser = false,
): OnboardingPath {
  void riskProfilerRequired
  if (isSuperuser) return POST_ONBOARDING_PATH
  if (!basicDetailsCompleted) return '/onboarding/basic-details'
  // Risk profiler is deferred: users can browse opportunities after basic details.
  return POST_ONBOARDING_PATH
}

export function resolveOnboardingPath(profile: Partial<BasicDetailsProfile>): OnboardingPath {
  return resolveOnboardingPathFromFlags(
    Boolean(profile.basicDetailsCompleted),
    resolveRiskProfilerRequired(profile),
  )
}

/** @deprecated Use resolveOnboardingPath */
export function resolvePostLoginPath(profile: Partial<BasicDetailsProfile>): OnboardingPath {
  return resolveOnboardingPath(profile)
}
